import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import DOMPurify from 'dompurify';
import { chatConnectAction, sendMessage } from '@/services/chat/chatActions';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import formatCreatedAt from '@/features/time-format/format-created-at';
import sendChat from '@/assets/sendChat.svg';
import elipsis from '@/assets/elipsis.svg';
import eye from '@/assets/eye.svg';
import { StreamType, TableStreamHeader } from '../TableStreams';
import { SystemMessages } from '@/services/chat/service/systemTypes';
import { ChatMessage } from '@/services/chat/chatServicesTypes';
import styles from './TableChat.module.css';
import useGetTableRest from '@/services/table/queries/useGetTableRest';
import useIsMobileView from '@/hooks/useIsMobileView';
import { twMerge } from 'tailwind-merge';
import debounce from 'lodash/debounce';

enum ChatMenuActions {
  MUTE = 'Mute',
  BLOCK = 'Block',
  REPORT = 'Report',
}
interface SystemMsgParams {
  systemMessage: SystemMessages;
}

const SystemMsg: React.FC<SystemMsgParams> = ({ systemMessage }) => {
  return (
    <div className="flex flex-col px-6">
      <div className="flex flex-row">
        <div className="text-[0.625em] font-semibold text-ph-purple">Phenom Poker</div>
        <div className="ml-auto text-right font-bold text-white text-opacity-30">
          <span>{formatCreatedAt(systemMessage.createdAt)}</span>
        </div>
      </div>
      <div className="text-sm text-xs font-normal text-ph-gray-dark">{systemMessage.message}</div>
    </div>
  );
};

const ChatMenuItem: React.FC<{
  disabled?: boolean;
  onClick?: () => void;
  text: string;
}> = ({ disabled, onClick, text }) => (
  <button
    disabled={disabled}
    onClick={onClick}
    className="group flex w-full items-center rounded-md p-[8px] px-[16px] text-left text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
  >
    {text}
  </button>
);

const TableChat: FC<{
  tableId: string;
  userId: string;
  setStreamType: (streamType: StreamType) => void;
  streamType?: StreamType.CHAT | StreamType.SYSTEM;
}> = ({ tableId, userId, setStreamType, streamType = StreamType.CHAT }) => {
  const isMobileView = useIsMobileView();
  const [postMessage, setPostMessage] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true); // State to track if the user is at the bottom
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null); // Reference to the chat container
  const chatMessages = useSelector((state: ReducerState) =>
    state.chats?.[tableId]?.chatMessages.filter((message) => {
      if (streamType === StreamType.CHAT) {
        return !!message.alias;
      } else {
        return !message.alias;
      }
    }),
  );

  // TODO: Add when chat message menu actions are available
  // const { data: tableInitData } = useGetTableRest(tableId);

  const dispatch = useDispatch();

  useEffect(() => {
    const handleScroll = debounce(() => {
      const chatContainer = chatContainerRef.current;
      if (chatContainer) {
        const MESSAGE_CONTAINER_HEIGHT = 32;
        const { scrollTop, scrollHeight, clientHeight } = chatContainer;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - MESSAGE_CONTAINER_HEIGHT;

        setIsAtBottom(isAtBottom);
      }
    }, 100); // Adjust the debounce wait time as needed

    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => chatContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [chatMessages, isAtBottom]);

  const handlePostMessage = (event: React.ChangeEvent<HTMLInputElement>) => setPostMessage(event.target.value);

  const sendPostMessage = () => {
    const trimmedMessage = postMessage.trim();
    if (trimmedMessage) {
      dispatch(sendMessage(tableId, DOMPurify.sanitize(trimmedMessage)));
      setPostMessage('');
    }
  };

  const reportUser = async (userId: string) => {
    console.log('report user', userId); // TODO
  };

  const blockUser = async (userId: string) => {
    console.log('block user', userId); // TODO
  };

  const ChatMsg = ({ index, message, userId }: { index: number; message: ChatMessage; userId: string }) => {
    return (
      <div
        key={index}
        className={`flex flex-col gap-0.5 px-6 hover:bg-gradient-hover active:bg-gradient-active ${message.userId === userId ? 'font-extrabold' : ''}`}
      >
        <div className="flex flex-row items-center">
          <div className="flew-row flex text-xs font-bold text-ph-purple text-white">
            <span className="flew-row flex">{message.alias}</span>
            {!message.isPlayer && (
              <span className="ml-1 flex items-center text-ph-orange">
                <img src={eye} alt="observer" className="mx-1" />
                Observer
              </span>
            )}
          </div>
          <div className="ml-auto text-right font-bold text-white text-opacity-30">
            <span className="truncate">{formatCreatedAt(message.createdAt)}</span>
            {/* TODO: Add menu actions when functionality is available. */}
            {/* {message.userId !== userId && (
              <Menu as="div" className="relative inline-block text-left ml-2">
                <Menu.Button className="hover:bg-black/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75">
                  <img
                    src={elipsis}
                    className="ml-2 w-[16px]"
                    alt="show actions"
                  />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items>
                    <div className="absolute min-w-[100px] z-20 top-[-10px] left-[-75px] rounded-[10px] p-[1px] bg-gradient-border">
                      <div className="bg-ph-violet-medium rounded-[10px] p-[8px] px-[16px]">
                        {Object.values(ChatMenuActions).map((action) => (
                          <Menu.Item key={action}>
                            <ChatMenuItem
                              onClick={() =>
                                dispatch(
                                  chatConnectAction(
                                    tableId,
                                    tableInitData?.data.url || "",
                                  ),
                                )
                              }
                              text={action}
                            />
                          </Menu.Item>
                        ))}
                      </div>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )} */}
          </div>
        </div>
        <div className="text-left text-sm font-semibold text-white text-opacity-50">{message.message}</div>
      </div>
    );
  };

  return (
    <div
      className={twMerge(
        'flex h-full min-h-0 w-full flex-1 flex-col rounded-[20px] text-ph-gray-silver',
        isMobileView ? 'text-[16px]' : '',
      )}
    >
      <TableStreamHeader selectedStreamType={streamType} setStreamType={setStreamType} />
      <div className={twMerge(styles.chatContent, 'custom-scroll hide-scrollbar')} ref={chatContainerRef}>
        {chatMessages?.map((message, index) => {
          if (message.alias) {
            return <ChatMsg key={index} message={message as ChatMessage} index={index} userId={userId} />;
          }

          return <SystemMsg key={index} systemMessage={message} />;
        })}
        <div ref={messagesEndRef} />
      </div>
      <div className="rounded-bl-[20px] rounded-br-[20px] bg-ph-purple-gray px-6 py-2">
        <div className="flex w-full items-center">
          <input
            className="w-full border-transparent bg-transparent text-left text-xs font-normal !outline-none focus:border-transparent focus:ring-0"
            placeholder="Start typing ..."
            onChange={handlePostMessage}
            onKeyDown={(event) => event.key === 'Enter' && sendPostMessage()}
            value={postMessage}
          />
          <button onClick={sendPostMessage} className="w-5 outline-none">
            <img src={sendChat} alt="send" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableChat;
